import React from 'react';
import groupBy from 'lodash/groupBy';
import { Table, TableBody, TableCell, TableHeader, TableRow } from 'semantic-ui-react';
import If from 'Views/Components/If/If';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { compareMeasurementsByTypeThenValue } from 'Util/_HumanWritten/MeasurementUtils';
import { PricingStrategyIds } from 'Global/Pricing/PricingStrategy';
import { PriceCellState } from 'Global/Pricing/PriceCellState';
import { PricingRouteTabs, SegmentHeaderCells } from './PricingRouteTabs';
import { UserPriceCell } from './UserPriceCell';
import { PricingStrategyKind, UserPriceRow } from './userPricingRows';

interface UserMeasurementsPricingTableProps {
	rows: UserPriceRow[];
	onCellWrite: (target: UserPriceRow, next: PriceCellState) => void;
	isReadOnly?: boolean;
	errors?: { [key: string]: string | undefined };
	pricingStrategy: PricingStrategyKind;
	trailerPricing: boolean;
}

/**
 * Form-state fork of the shared MeasurementsTable (vehicle and trailer variants via
 * trailerPricing). Missing per-segment slots render as virtual rows and materialise on first
 * edit — and unlike the legacy table's known bug, a trailer-mode virtual row uses the trailer
 * measurement fields, so it persists on the correct dimension.
 */
export function UserMeasurementsPricingTable({
	rows,
	onCellWrite,
	isReadOnly = false,
	errors,
	pricingStrategy,
	trailerPricing,
}: UserMeasurementsPricingTableProps) {
	const measurementIdOf = (row: UserPriceRow) => (trailerPricing ? row.trailerMeasurementId : row.measurementId);
	const measurementOf = (row: UserPriceRow) => (trailerPricing ? row.trailerMeasurement : row.measurement);

	return (
		<PricingRouteTabs pricingStrategy={pricingStrategy}>
			{(route, segmentsSorted) => (
				<Table unstackable>
					<TableHeader>
						<TableRow key={0}>
							<TableCell textAlign="left">Ticket type</TableCell>
							<If condition={pricingStrategy === 'Fixed'}>
								<TableCell textAlign="left">Cost</TableCell>
							</If>
							<If condition={pricingStrategy === 'ConsecutiveStops'}>
								<SegmentHeaderCells segments={segmentsSorted} />
							</If>
						</TableRow>
					</TableHeader>
					<TableBody>
						{Object.entries(
							groupBy(
								rows
									.filter(
										row =>
											isNotNullOrUndefined(measurementIdOf(row)) &&
											((isNullOrUndefined(route.id) && isNullOrUndefined(row.routeId)) ||
												row.routeId === route.id),
									)
									.slice()
									.sort((value1, value2) => {
										const m1 = measurementOf(value1);
										const m2 = measurementOf(value2);
										return compareMeasurementsByTypeThenValue(
											{ measurementType: m1?.measurementType, value: m1?.value },
											{ measurementType: m2?.measurementType, value: m2?.value },
										);
									}),
								row => measurementIdOf(row),
							),
						).map(([key, groupedRows]) => {
							return (
								<TableRow key={key}>
									<TableCell textAlign="left">{measurementOf(groupedRows[0])?.label}</TableCell>
									<If condition={pricingStrategy === 'Fixed'}>
										<TableCell textAlign="left">
											<UserPriceCell
												row={groupedRows[0]}
												onWrite={next => onCellWrite(groupedRows[0], next)}
												isReadOnly={isReadOnly}
												error={!!errors?.amount}
											/>
										</TableCell>
									</If>
									<If condition={pricingStrategy === 'ConsecutiveStops'}>
										{segmentsSorted.map(segment => {
											const found = groupedRows.find(p => p.startStopId === segment.startStop.id);
											const measurement = measurementOf(groupedRows[0]);
											const cellRow: UserPriceRow = found ?? {
												...(trailerPricing
													? { trailerMeasurementId: key, trailerMeasurement: measurement }
													: { measurementId: key, measurement }),
												routeId: route.id,
												startStopId: segment.startStop.id,
												amount: null,
												mode: 'FIXED',
												pricingStrategyId: PricingStrategyIds[pricingStrategy],
											};
											return (
												<TableCell textAlign="left" key={segment.id}>
													<UserPriceCell
														row={cellRow}
														onWrite={next => onCellWrite(cellRow, next)}
														isReadOnly={isReadOnly}
														error={!!errors?.amount}
													/>
												</TableCell>
											);
										})}
									</If>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			)}
		</PricingRouteTabs>
	);
}
